import React, { useCallback, useEffect, useRef, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useLocale, useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import type { Lot } from '../../types/lot'
import { lotApi } from '../../utils/lotApi'
import { isLowConfidence, LOW_COMPLETENESS_PCT, type LotProjection, type LotProjectionCaution } from '../../utils/lotProjection'
import { readHarvestTargetWeightG, withHarvestTargetWeightG } from '../../utils/lotProjectionSettings'
import { loadLotProjections } from '../../utils/reportDatasets'
import { refreshLotSourcesFromServer } from '../../utils/refreshLotSources'

// Lot projections (findings #82): where an Active lot is heading — harvest
// date, biomass and stock at harvest, feed to harvest — from its own
// samplings, water temperatures and recorded mortality. Office-side only.

const STATUS_KEY: Record<Exclude<LotProjection['status'], 'ok'>, TranslationKey> = {
  noSampling: 'lots.projection.status.noSampling',
  noGrowthRate: 'lots.projection.status.noGrowthRate',
  noTemperature: 'lots.projection.status.noTemperature',
  noStock: 'lots.projection.status.noStock',
  hatcheryStage: 'lots.projection.status.hatcheryStage'
}

const CAUTION_KEY: Record<LotProjectionCaution, TranslationKey> = {
  lowMortalityCompleteness: 'lots.projection.caution.lowMortalityCompleteness',
  noMortalityRecords: 'lots.projection.caution.noMortalityRecords',
  officeTgc: 'lots.projection.caution.officeTgc',
  oldSampling: 'lots.projection.caution.oldSampling',
  noTarget: 'lots.projection.caution.noTarget',
  targetReached: 'lots.projection.caution.targetReached',
  horizonReached: 'lots.projection.caution.horizonReached',
  noSfr: 'lots.projection.caution.noSfr',
  unusualFcr: 'lots.projection.caution.unusualFcr',
  estimatedStartWeight: 'lots.projection.caution.estimatedStartWeight'
}

const labelStyle: React.CSSProperties = { fontSize: 12, color: '#047857' }
const valueStyle: React.CSSProperties = { fontSize: 14, color: '#0f172a', fontWeight: 700 }

export type LotProjectionSectionProps = {
  lot: Lot
  facilityTargetWeightG: number | null
  canEdit: boolean
}

export default function LotProjectionSection({ lot, facilityTargetWeightG, canEdit }: LotProjectionSectionProps) {
  const t = useT()
  const locale = useLocale()
  const [projection, setProjection] = useState<LotProjection | null>(null)
  const [loading, setLoading] = useState(true)
  const [targetDraft, setTargetDraft] = useState('')
  const [editingTarget, setEditingTarget] = useState(false)
  const [targetError, setTargetError] = useState<string | null>(null)
  const [lotTargetG, setLotTargetG] = useState<number | null>(() => readHarvestTargetWeightG(lot))

  // Findings #157: cached figures first, then the crew's latest records from the server.
  const loadSeqRef = useRef(0)
  const load = useCallback(async (fromServer = false) => {
    const seq = loadSeqRef.current + 1
    loadSeqRef.current = seq
    setLoading(true)
    try {
      const [row] = await loadLotProjections({ lotIds: [lot.id] })
      if (loadSeqRef.current === seq) setProjection(row?.projection ?? null)
    } catch (error) {
      console.warn('[lots] projection failed', error)
      if (loadSeqRef.current === seq) setProjection(null)
    } finally {
      if (loadSeqRef.current === seq) setLoading(false)
    }
    if (!fromServer) return
    const pulled = await refreshLotSourcesFromServer(lot.id).catch(() => false)
    if (!pulled || loadSeqRef.current !== seq) return
    try {
      const [row] = await loadLotProjections({ lotIds: [lot.id] })
      if (loadSeqRef.current === seq) setProjection(row?.projection ?? null)
    } catch (error) {
      console.warn('[lots] projection failed', error)
    }
  }, [lot.id])

  useEffect(() => {
    setLotTargetG(readHarvestTargetWeightG(lot))
    void load(true)
  }, [lot, load])

  const number = (value: number | null, digits = 0) => (
    value === null ? '—' : value.toLocaleString(locale, { maximumFractionDigits: digits })
  )
  const formatDay = (isoDay: string | null) => {
    if (!isoDay) return '—'
    const date = new Date(`${isoDay}T12:00:00Z`)
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }

  const saveTarget = async (grams: number | null) => {
    setTargetError(null)
    try {
      const next = withHarvestTargetWeightG(lot, grams)
      await lotApi.update(lot.id, { metadata: next.metadata })
      setLotTargetG(grams)
      setEditingTarget(false)
      await load()
    } catch (error) {
      console.warn('[lots] failed to save harvest target', error)
      setTargetError(t('lots.projection.target.saveFailed'))
    }
  }

  const handleSaveTarget = () => {
    const trimmed = targetDraft.trim().replace(',', '.')
    if (!trimmed) {
      void saveTarget(null)
      return
    }
    const grams = Number(trimmed)
    if (!Number.isFinite(grams) || grams <= 0 || grams > 50000) {
      setTargetError(t('lots.projection.target.invalid'))
      return
    }
    void saveTarget(grams)
  }

  const targetLabel = lotTargetG !== null
    ? t('lots.projection.target.fromLot', { grams: number(lotTargetG) })
    : facilityTargetWeightG !== null
      ? t('lots.projection.target.fromFacility', { grams: number(facilityTargetWeightG) })
      : t('lots.projection.target.none')

  return (
    <div
      data-testid="lot-projection-section"
      style={{ marginBottom: 12, padding: 12, borderRadius: 10, border: '1px solid #a7f3d0', backgroundColor: '#f0fdf4', display: 'grid', gap: 10 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong style={{ fontSize: 13, color: '#047857' }}>{t('lots.projection.title')}</strong>
        {projection && projection.status === 'ok' && isLowConfidence(projection) ? (
          <span data-testid="lot-projection-low-confidence" style={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>
            {t('lots.projection.lowConfidence')}
          </span>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
        <span style={labelStyle}>{t('lots.projection.target.label')}:</span>
        {editingTarget ? (
          <>
            <input
              data-testid="lot-projection-target-input"
              inputMode="decimal"
              value={targetDraft}
              placeholder={facilityTargetWeightG !== null ? String(facilityTargetWeightG) : ''}
              onChange={(event) => setTargetDraft(event.target.value)}
              style={{ width: 110, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8 }}
            />
            <span>g</span>
            <GradientButton size="xs" variant="success" onClick={handleSaveTarget} data-testid="lot-projection-target-save">
              {t('common.save')}
            </GradientButton>
            <GradientButton size="xs" variant="secondary" onClick={() => setEditingTarget(false)}>
              {t('common.cancel')}
            </GradientButton>
            <span style={{ fontSize: 12, color: '#64748b' }}>{t('lots.projection.target.help')}</span>
          </>
        ) : (
          <>
            <span data-testid="lot-projection-target" style={{ fontWeight: 700 }}>{targetLabel}</span>
            {canEdit ? (
              <GradientButton
                size="xs"
                variant="info"
                onClick={() => {
                  setTargetDraft(lotTargetG !== null ? String(lotTargetG) : '')
                  setTargetError(null)
                  setEditingTarget(true)
                }}
                data-testid="lot-projection-target-edit"
              >
                {t('lots.projection.target.edit')}
              </GradientButton>
            ) : null}
          </>
        )}
      </div>
      {targetError ? <div role="alert" style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{targetError}</div> : null}

      {loading ? (
        <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.projection.loading')}</div>
      ) : !projection ? (
        <div style={{ fontSize: 13, color: '#475569' }}>{t('lots.projection.unavailable')}</div>
      ) : projection.status !== 'ok' ? (
        <div data-testid="lot-projection-status" style={{ fontSize: 13, color: '#92400e' }}>{t(STATUS_KEY[projection.status])}</div>
      ) : (
        <>
          <div data-testid="lot-projection-figures" style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.projection.harvest')}</span>
              <span style={valueStyle} data-testid="lot-projection-harvest">
                {projection.harvestDate
                  ? t('lots.projection.harvestValue', { date: formatDay(projection.harvestDate), days: number(projection.daysToHarvest) })
                  : '—'}
              </span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.projection.weightToday')}</span>
              <span style={valueStyle}>{number(projection.estimatedWeightTodayG, 1)} g</span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.projection.weightIn30')}</span>
              <span style={valueStyle}>{number(projection.weightIn30DaysG, 1)} g</span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.projection.biomass')}</span>
              <span style={valueStyle}>
                {number(projection.biomassNowKg)} → {number(projection.biomassAtHarvestKg)} kg
              </span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.projection.stockAtHarvest')}</span>
              <span style={valueStyle}>{number(projection.stockAtHarvest)}</span>
            </div>
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={labelStyle}>{t('lots.projection.feedToHarvest')}</span>
              <span style={valueStyle} data-testid="lot-projection-feed">
                {number(projection.feedToHarvestKg)} kg
                {projection.projectedFcr !== null ? ` · FCR ${number(projection.projectedFcr, 2)}` : ''}
              </span>
            </div>
          </div>

          <div data-testid="lot-projection-inputs" style={{ fontSize: 12, color: '#475569', display: 'grid', gap: 2 }}>
            <span>
              {t(projection.tgcSource === 'observed' ? 'lots.projection.input.tgcObserved' : 'lots.projection.input.tgcOffice', {
                tgc: number(projection.tgc, 2),
                date: formatDay(projection.lastSamplingDate),
                weight: number(projection.lastSamplingWeightG, 1)
              })}
            </span>
            <span>{t('lots.projection.input.temperature', { temperature: number(projection.meanTemperatureC, 1) })}</span>
            <span data-testid="lot-projection-mortality">
              {t('lots.projection.input.mortality', {
                rate: projection.mortalityRatePctPerDay === null ? '—' : number(projection.mortalityRatePctPerDay, 3),
                recorded: projection.mortalityDaysRecorded,
                total: projection.mortalityWindowDays
              })}
              {projection.mortalityCompletenessPct !== null && projection.mortalityCompletenessPct < LOW_COMPLETENESS_PCT
                ? <strong style={{ color: '#b45309' }}> · {t('lots.projection.input.lowCompleteness', { pct: LOW_COMPLETENESS_PCT })}</strong>
                : null}
            </span>
          </div>

          {projection.cautions.length > 0 ? (
            <ul data-testid="lot-projection-cautions" style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#92400e' }}>
              {projection.cautions.map((caution) => <li key={caution}>{t(CAUTION_KEY[caution])}</li>)}
            </ul>
          ) : null}
        </>
      )}
    </div>
  )
}
